import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import { QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { type ErrorType } from '../shared/shared.type'
import {
  type CreateInvestorResponse,
  type InvestorPayload,
  type UpdateInvestorPayload,
  type CurrentInvestorResponse,
  UpdatedInvestor,
  Investor,
  IAssignedFulfillmentCentre,
  IAssignFulfilmentCentre,
  IFC,
  IUpdateFulfilmentCentreSettings,
} from './investor.type'

export const investorApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllInvestors: builder.query<CurrentInvestorResponse, QueryProps>({
      query: (args) => ({
        url: `v2/stakeholders`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'STAKEHOLDER', id: 'LIST' }],
    }),
    getInvestor: builder.query<Investor, { id: string }>({
      query: ({ id }) => ({
        url: `v2/stakeholders/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: Investor }) => response.data,
      providesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg.id },
      ],
    }),
    createInvestor: builder.mutation<CurrentInvestorResponse, InvestorPayload>({
      query: (payload) => ({
        url: `v2/stakeholders`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'STAKEHOLDER', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Stakeholders added successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateInvestor: builder.mutation<
      CurrentInvestorResponse,
      { body: UpdatedInvestor; id: string }
    >({
      query: (payload) => ({
        url: `v2/stakeholders/${payload.id}`,
        method: 'PUT',
        body: payload.body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg.id },
        { type: 'STAKEHOLDER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    activateInvestor: builder.mutation<null, { id: number; active: boolean }>({
      query: ({ id, active }) => ({
        url: `v2/stakeholders/activate/${id}`,
        method: 'PUT',
        body: { active },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg.id },
        { type: 'STAKEHOLDER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteInvestor: builder.mutation<null, number>({
      query: (id) => ({
        url: `v2/stakeholders/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg },
        { type: 'STAKEHOLDER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User deleted successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    inviteInvestor: builder.mutation<null, string>({
      query: (id) => ({
        url: `v2/stakeholders/${id}/send-setup-link`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg },
        { type: 'STAKEHOLDER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Investor invited successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deactivateActivateInvestor: builder.mutation<
      CreateInvestorResponse,
      { payload: UpdateInvestorPayload; id: string }
    >({
      query: ({ payload, id }) => ({
        url: `v1/admin/updateadmin/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      transformResponse: (response: CreateInvestorResponse) => response,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'STAKEHOLDER', id: arg.id },
        { type: 'STAKEHOLDER', id: 'LIST' },
        { type: 'USER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success(
            `Investor ${
              arg.payload.deleted ? 'deactivated' : 'activated'
            } successfully.`,
          )
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    assignFulfillmentCentre: builder.mutation<
      IAssignedFulfillmentCentre,
      { payload: IAssignFulfilmentCentre; stakeholderId: string }
    >({
      query: ({ payload, stakeholderId }) => ({
        url: `v2/stakeholders/${stakeholderId}/fc-assignments`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
        { type: 'STAKEHOLDER', id: arg.stakeholderId },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success(
            'Stakeholders assigned to fulfillment centre successfully.',
          )
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    updateAssignFulfillmentCentre: builder.mutation<
      CurrentInvestorResponse,
      { payload: IUpdateFulfilmentCentreSettings; id: string; fcId: string }
    >({
      query: ({ payload, id, fcId }) => ({
        url: `v2/stakeholders/${id}/fc-assignments/${fcId}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: arg.fcId },
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
        { type: 'STAKEHOLDER', id: arg.id },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Stakeholders update fulfillment centre successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),

    deleteAssignFulfillmentCentre: builder.mutation<
      null,
      { id: string; fcId: string }
    >({
      query: ({ id, fcId }) => ({
        url: `v2/stakeholders/${id}/fc-assignments/${fcId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'FULFILMENT_CENTRE', id: arg.fcId },
        { type: 'FULFILMENT_CENTRE', id: 'LIST' },
        { type: 'STAKEHOLDER', id: arg.id },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Fulfillment Centre deleted successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAssignedFulfillmentCentres: builder.query<IFC[], { id: string }>({
      query: ({ id }) => ({
        url: `v2/stakeholders/${id}/fc-assignments`,
        method: 'GET',
      }),
      transformResponse: (response: { data: IFC[] }) => response.data,
      providesTags: [{ type: 'FULFILMENT_CENTRE', id: 'LIST' }],
    }),
  }),
})

export const {
  useGetAllInvestorsQuery,
  useGetInvestorQuery,
  useCreateInvestorMutation,
  useDeleteInvestorMutation,
  useUpdateInvestorMutation,
  useActivateInvestorMutation,
  useInviteInvestorMutation,
  useDeactivateActivateInvestorMutation,
  useAssignFulfillmentCentreMutation,
  useUpdateAssignFulfillmentCentreMutation,
  useDeleteAssignFulfillmentCentreMutation,
  useGetAssignedFulfillmentCentresQuery,
} = investorApiSlice
